import React, { useContext, useEffect, useState } from "react";
import { 
    getRedirectResult,
    isSignInWithEmailLink,
    signInWithRedirect,
    sendSignInLinkToEmail,
    signInWithEmailLink,
    signOut
} from 'firebase/auth';
import { auth, provider } from "../firebase";
import { ToastContext } from "./ToastContext";

export const AuthContext = React.createContext();

export const AuthProvider = ({ children }) => {
    const { toast } = useContext(ToastContext);
    const [ user, setUser ] = useState(null);
    const [ isLoggedIn, setIsLoggedIn ] = useState(false);
    const [ authLoading, setAuthLoading ] = useState(false);
    const [ authError, setAuthError ] = useState('');
    const [ authMessage, setAuthMessage ] = useState('');

    const clearAuthFeedback = () => {
        setAuthError('');
        setAuthMessage('');
    };

    const loginWithEmail = (email) => {
        const trimmedEmail = email.trim();

        if (!trimmedEmail || !/^\S+@\S+\.\S+$/.test(trimmedEmail)) {
            setAuthError('Please enter a valid email address.');
            setAuthMessage('');
            return Promise.resolve(false);
        }

        setAuthLoading(true);
        setAuthError('');
        setAuthMessage('');

        return sendSignInLinkToEmail(auth, trimmedEmail, {
            url: `${window.location.origin}`,
            handleCodeInApp: true
        })
        .then(() => {
            toast(`Sign-in link sent to ${trimmedEmail}`);
            setAuthMessage(`Magic link sent to ${trimmedEmail}. Check your inbox.`);
            window.localStorage.setItem('emailForSignIn', trimmedEmail);
            return true;
        })
        .catch((error) => {
            const message = error?.message || 'Unable to send a sign-in link right now.';
            setAuthError(message);
            return false;
        })
        .finally(() => {
            setAuthLoading(false);
        });
    };

    const loginWithGoogle = () => {
        setAuthLoading(true);
        setAuthError('');
        setAuthMessage('');

        signInWithRedirect(auth, provider)
        .then(r => {
            console.log(r);
        })
        .catch(e => {
            setAuthError(e?.message || 'Google sign-in failed.');
        })
        .finally(() => {
            setAuthLoading(false);
        });
    };

    const logout = () => {
        setAuthError('');
        setAuthMessage('');

        signOut(auth).then(() => {
            setUser(null);
        });
    };

    useEffect(() => {
        if (isSignInWithEmailLink(auth, window.location.href)) {
            let email = window.localStorage.getItem('emailForSignIn');
            if (!email) {
                email = window.prompt('Please provide your email for confirmation');
            }

            signInWithEmailLink(auth, email, window.location.href)
            .then((result) => {
                window.localStorage.removeItem('emailForSignIn');
                if (result?.user != null) {
                    setUser(result.user.toJSON());
                }
              })
        } else {
            getRedirectResult(auth).then(result => {
                if (result?.user != null) {
                    setUser(result.user.toJSON());
                } else if (auth.currentUser != null) {
                    setUser(auth.currentUser);
                }
            })
            .catch(e => {
                console.log(e);
            });
        }
    }, [])

    useEffect(() => {
        setIsLoggedIn(user !== null);
    }, [user]);

    return (
        <AuthContext.Provider value={{
            user,
            loginWithEmail,
            loginWithGoogle,
            logout,
            isLoggedIn,
            authLoading,
            authError,
            authMessage,
            clearAuthFeedback,
        }}>
            { children }
        </AuthContext.Provider>
    );
}